import * as React from 'react';
import {
  Box,
  Icon,
  ParagraphText,
  StackLayout,
  TextAction,
  UtilityText,
} from '@leagueplatform/genesis-core';
import { useIntl } from '@leagueplatform/locales';
import { NonLoadingStatus, statusIconMap } from '../../constants';

const statusTextColorMap: Record<NonLoadingStatus, string> = {
  success: '$successIcon',
  skipped: '$highlightIcon',
  error: '$criticalIcon',
  unprocessed: '$onSurfaceTextSubdued',
};

/**
 * The subset of statuses that can be used as filters. Excludes 'unprocessed'
 * because unprocessed items are not shown in the status report table.
 */
export type StatusSummaryFilterStatus = Exclude<
  NonLoadingStatus,
  'unprocessed'
>;

export interface StatusSummaryProps {
  primaryMessage?: React.ReactNode;
  // Primary message uses default text styling unless it needs the error treatment.
  status?: 'error';
  successCount?: number;
  skippedCount?: number;
  failedCount?: number;
  unprocessedCount?: number;
  /**
   * When provided, count items (excluding 'unprocessed') become interactive
   * filter buttons. Called with the clicked status, or null when the active
   * filter is clicked again to clear it.
   */
  onStatusFilterChange?: (status: StatusSummaryFilterStatus | null) => void;
  /** The currently active status filter, if any. */
  activeStatusFilter?: StatusSummaryFilterStatus | null;
}

const StatusSegment = ({
  label,
  status,
  isActive = false,
}: {
  label: string;
  status: NonLoadingStatus;
  isActive?: boolean;
}) => {
  const iconDetails = statusIconMap[status];
  return (
    <StackLayout
      orientation="horizontal"
      spacing={iconDetails.icon ? '$half' : '$none'}
      verticalAlignment="center"
      css={{
        borderBlockEnd: isActive ? '1px solid' : 'none',
        '&:hover': { borderBlockEnd: '1px solid' },
      }}
    >
      {iconDetails.icon ? (
        <Icon
          icon={iconDetails.icon}
          tint={iconDetails.tint}
          size="$threeQuarters"
        />
      ) : null}
      <UtilityText css={{ color: statusTextColorMap[status] }}>
        {label}
      </UtilityText>
    </StackLayout>
  );
};

const filterableStatuses = new Set<NonLoadingStatus>([
  'success',
  'skipped',
  'error',
]);

const isFilterableStatus = (
  itemStatus: NonLoadingStatus,
): itemStatus is StatusSummaryFilterStatus =>
  filterableStatuses.has(itemStatus);

export const StatusSummary = ({
  primaryMessage,
  status,
  successCount,
  skippedCount,
  failedCount,
  unprocessedCount,
  onStatusFilterChange,
  activeStatusFilter,
}: StatusSummaryProps) => {
  const { formatMessage } = useIntl();
  const statusItemConfig = [
    {
      id: 'success',
      count: successCount,
      labelId: 'STATUS_PROGRESS_SUCCESSFUL_COUNT',
      status: 'success',
    },
    {
      id: 'skipped',
      count: skippedCount,
      labelId: 'STATUS_PROGRESS_SKIPPED_COUNT',
      status: 'skipped',
    },
    {
      id: 'failed',
      count: failedCount,
      labelId: 'STATUS_PROGRESS_FAILED_COUNT',
      status: 'error',
    },
    {
      id: 'unprocessed',
      count: unprocessedCount,
      labelId: 'STATUS_PROGRESS_UNPROCESSED_COUNT',
      status: 'unprocessed',
    },
  ] as const;

  const statusItems = statusItemConfig
    .filter((item) => typeof item.count === 'number')
    .map((item) => ({
      id: item.id,
      label: formatMessage({ id: item.labelId }, { count: item.count }),
      status: item.status,
    }));

  if (!primaryMessage && statusItems.length === 0) {
    return null;
  }

  const isFilterable = Boolean(onStatusFilterChange);

  return (
    <StackLayout orientation="horizontal">
      {primaryMessage ? (
        <Box>
          <ParagraphText
            css={status === 'error' ? { color: '$criticalIcon' } : undefined}
          >
            {primaryMessage}
          </ParagraphText>
        </Box>
      ) : null}
      {statusItems.length > 0 ? (
        <StackLayout
          as="ul"
          orientation="horizontal"
          spacing="$none"
          css={{
            margin: '$none',
            paddingInlineStart: '$none',
            listStyleType: 'none',
          }}
        >
          {statusItems.map((item, index) => {
            // Capture a narrowed copy so TypeScript can verify the type inside
            // the onClick callback, where the outer isFilterableStatus guard
            // does not carry forward.
            const filterableStatus = isFilterableStatus(item.status)
              ? item.status
              : null;
            const canFilter = isFilterable && filterableStatus !== null;
            const isActive = activeStatusFilter === item.status;

            const segment = (
              <StatusSegment
                label={item.label}
                status={item.status}
                isActive={isActive}
              />
            );

            return (
              <Box
                as="li"
                key={item.id}
                css={{
                  display: 'flex',
                }}
              >
                {(index > 0 || primaryMessage) && (
                  <UtilityText
                    as="span"
                    aria-hidden
                    css={{ paddingInline: '$half' }}
                  >
                    |
                  </UtilityText>
                )}
                {canFilter && filterableStatus !== null ? (
                  <TextAction
                    as="button"
                    type="button"
                    aria-pressed={isActive}
                    onClick={() =>
                      onStatusFilterChange?.(isActive ? null : filterableStatus)
                    }
                    css={{
                      // Reset TextAction's interactive text color so
                      // StatusSegment's own status color tokens show through.
                      color: 'inherit',
                      '&:hover': {
                        textDecoration: 'none',
                      },
                    }}
                  >
                    {segment}
                  </TextAction>
                ) : (
                  segment
                )}
              </Box>
            );
          })}
        </StackLayout>
      ) : null}
    </StackLayout>
  );
};
